import * as CookieConsent from 'vanilla-cookieconsent';
import { GTM_ID, SITE_HOST } from './constants';

const getTranslations = (lang) => {
  const translations = {
    en: {
      consentModal: {
        title: 'Cookie settings',
        description:
          'We use cookies to store your preferences and analyze site traffic. By clicking "Accept all", you consent to our use of analytics cookies.',
        acceptAllBtn: 'Accept all',
        acceptNecessaryBtn: 'Reject all',
        showPreferencesBtn: 'Manage preferences',
        footer:
          '<a href="/privacy">Privacy Policy</a> <span style="margin: 0 0.3rem;">|</span> <a href="/legal-notice">Legal Notice</a>',
      },
      preferencesModal: {
        title: 'Cookie preferences',
        acceptAllBtn: 'Accept all',
        acceptNecessaryBtn: 'Reject all',
        savePreferencesBtn: 'Save preferences',
        closeIconLabel: 'Close',
        sections: [
          {
            title: 'Cookie usage',
            description:
              'We use cookies to store your consent preferences and, only if you agree, to analyze website traffic. Nothing is sent to Google before you accept.',
          },
          {
            title: 'Strictly necessary',
            description:
              'This cookie stores your consent preferences. It is essential for respecting your privacy choices and cannot be disabled.',
            linkedCategory: 'necessary',
            cookieTable: {
              headers: {
                name: 'Name',
                domain: 'Domain',
                expiration: 'Expiration',
                description: 'Description',
              },
              body: [
                {
                  name: 'cc_cookie',
                  domain: 'hinterland.software',
                  expiration: '6 months',
                  description: 'Stores your cookie consent preferences',
                },
              ],
            },
          },
          {
            title: 'Analytics',
            description:
              'These cookies are set by Google Analytics (via Google Tag Manager) to help us understand how visitors use the website. The data is collected anonymously.',
            linkedCategory: 'analytics',
            cookieTable: {
              headers: {
                name: 'Name',
                domain: 'Domain',
                expiration: 'Expiration',
                description: 'Description',
              },
              body: [
                {
                  name: '_ga',
                  domain: 'hinterland.software',
                  expiration: '2 years',
                  description: 'Google Analytics: Distinguishes unique users',
                },
                {
                  name: '_ga_*',
                  domain: 'hinterland.software',
                  expiration: '2 years',
                  description: 'Google Analytics: Maintains session state',
                },
                {
                  name: '_gid',
                  domain: 'hinterland.software',
                  expiration: '24 hours',
                  description: 'Google Analytics: Distinguishes users',
                },
              ],
            },
          },
        ],
      },
    },
    de: {
      consentModal: {
        title: 'Cookie-Einstellungen',
        description:
          'Wir verwenden Cookies, um Ihre Einstellungen zu speichern und den Website-Traffic zu analysieren. Mit "Alle akzeptieren" stimmen Sie der Verwendung von Analyse-Cookies zu.',
        acceptAllBtn: 'Alle akzeptieren',
        acceptNecessaryBtn: 'Alle ablehnen',
        showPreferencesBtn: 'Einstellungen verwalten',
        footer:
          '<a href="/datenschutz">Datenschutz</a> <span style="margin: 0 0.3rem;">|</span> <a href="/impressum">Impressum</a>',
      },
      preferencesModal: {
        title: 'Cookie-Einstellungen',
        acceptAllBtn: 'Alle akzeptieren',
        acceptNecessaryBtn: 'Alle ablehnen',
        savePreferencesBtn: 'Einstellungen speichern',
        closeIconLabel: 'Schließen',
        sections: [
          {
            title: 'Cookie-Nutzung',
            description:
              'Wir verwenden Cookies, um Ihre Einwilligungen zu speichern und, nur mit Ihrer Zustimmung, den Website-Traffic zu analysieren. Vor Ihrer Zustimmung werden keine Daten an Google übermittelt.',
          },
          {
            title: 'Notwendig',
            description:
              'Dieses Cookie speichert Ihre Einwilligungseinstellungen. Es ist für die Wahrung Ihrer Datenschutzentscheidungen unerlässlich und kann nicht deaktiviert werden.',
            linkedCategory: 'necessary',
            cookieTable: {
              headers: {
                name: 'Name',
                domain: 'Domain',
                expiration: 'Gültigkeit',
                description: 'Beschreibung',
              },
              body: [
                {
                  name: 'cc_cookie',
                  domain: 'hinterland.software',
                  expiration: '6 Monate',
                  description: 'Speichert Ihre Cookie-Einwilligungen',
                },
              ],
            },
          },
          {
            title: 'Analyse',
            description:
              'Diese Cookies werden von Google Analytics (über Google Tag Manager) gesetzt, um zu verstehen, wie Besucher die Website nutzen. Die Daten werden anonym erhoben.',
            linkedCategory: 'analytics',
            cookieTable: {
              headers: {
                name: 'Name',
                domain: 'Domain',
                expiration: 'Gültigkeit',
                description: 'Beschreibung',
              },
              body: [
                {
                  name: '_ga',
                  domain: 'hinterland.software',
                  expiration: '2 Jahre',
                  description:
                    'Google Analytics: Unterscheidet eindeutige Nutzer',
                },
                {
                  name: '_ga_*',
                  domain: 'hinterland.software',
                  expiration: '2 Jahre',
                  description: 'Google Analytics: Erhält den Sitzungsstatus',
                },
                {
                  name: '_gid',
                  domain: 'hinterland.software',
                  expiration: '24 Stunden',
                  description: 'Google Analytics: Unterscheidet Nutzer',
                },
              ],
            },
          },
        ],
      },
    },
  };

  return translations[lang] || translations.en;
};

// gtag() must push the real `arguments` object, not a rest array, or GTM
// ignores the command.
function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}
// Tags inside the container may call gtag() themselves, as the old inline
// script allowed.
window.gtag = gtag;

let gtmLoaded = false;

// Tag Manager is only fetched once analytics is accepted, so no request goes
// to Google for visitors who decline.
const loadGtm = () => {
  if (gtmLoaded) return;
  gtmLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
};

const updateConsent = (granted) => {
  gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  });
  if (granted) {
    loadGtm();
  } else if (gtmLoaded) {
    // A loaded container cannot be unloaded; the cookie library has already
    // cleared the _ga cookies, so a reload gives the visitor a page without it.
    window.location.reload();
  }
};

export const initConsent = (lang = 'en') => {
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  // One consent for both language subdomains; local and preview hosts keep
  // the default host-only cookie.
  const onSiteHost =
    window.location.hostname === SITE_HOST ||
    window.location.hostname.endsWith(`.${SITE_HOST}`);

  // Consent used to be stored host-only per subdomain. Browsers send the
  // older cookie first, and the library reads the first match, so a stale
  // acceptance would shadow a fresh rejection. Dropping the host-only copy
  // asks returning visitors once more, which is the safer of the two.
  if (onSiteHost) {
    document.cookie = 'cc_cookie=; Max-Age=0; path=/; SameSite=Lax; Secure';
  }

  CookieConsent.run({
    cookie: onSiteHost ? { domain: SITE_HOST } : {},

    guiOptions: {
      consentModal: {
        layout: 'box wide',
        position: 'bottom center',
      },
      preferencesModal: {
        layout: 'box',
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        autoClear: {
          // GA writes its cookies on the registrable domain, not on the
          // language subdomain the page runs on, so both scopes are cleared.
          cookies: [
            { name: /^_ga/ },
            { name: '_gid' },
            { name: /^_ga/, domain: SITE_HOST },
            { name: '_gid', domain: SITE_HOST },
          ],
        },
      },
    },

    language: {
      default: lang,
      translations: {
        en: getTranslations('en'),
        de: getTranslations('de'),
      },
    },

    onConsent: () => {
      updateConsent(CookieConsent.acceptedCategory('analytics'));
    },

    onChange: () => {
      updateConsent(CookieConsent.acceptedCategory('analytics'));
    },
  });
};

export default initConsent;
